import React, { useState, useEffect } from 'react';
import Game from '../components/main/Game';
import Header from '../components/main/Header';
import Footer from '../components/main/Footer';
import { getBasicData } from '../helper/userHelperFunctions';
import './styles/Main.css';
import EndModal from '../components/modals/EndModal';
import { getTodaysBoard, getBoardByBoardNumber, submitUserData } from '../services/api';
import { getGameDataProperty } from '../services/state';

const MAX_BOARD_HISTORY = 6;

const buildBoardOptions = (latestBoardNumber) => {
    if (!latestBoardNumber || latestBoardNumber < 1) return [];
    const count = Math.min(MAX_BOARD_HISTORY, latestBoardNumber);
    return Array.from({ length: count }, (_, index) => latestBoardNumber - index);
};

const Main = () => {
    const [basicUserData, setBasicUserData] = useState({});
    const [currentBoardData, setCurrentBoardData] = useState({});
    const [latestBoardNum, setLatestBoardNum] = useState(null);
    const [showEndModal, setShowEndModal] = useState(false);
    const [gameDone, setGameDone] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showBoardMenu, setShowBoardMenu] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [pendingBoardChoice, setPendingBoardChoice] = useState(null);

    useEffect(() => {
        const fetchBasicData = async () => {
            try {
                const response = await getBasicData();
                setBasicUserData(response);

                // // Submit the user data to the server
                submitUserData({ user_browser_id: response.userId });
            } catch (err) {
                console.error('Error fetching basic data:', err);
            }
        };

        const fetchBoardData = async () => {
            try {
                const todaysBoard = await getTodaysBoard();
                const boardNum = todaysBoard['board_num'];
                setLatestBoardNum(boardNum);
                setCurrentBoardData({
                    boardNum,
                    targetNums: todaysBoard['target_nums'],
                    operators: todaysBoard['operators'],
                    solution_length: todaysBoard['solution_length']
                });
                const playedGame = getGameDataProperty(boardNum, 'gameScore') !== null;
                setGameDone(playedGame);
                setShowEndModal(playedGame);
            } catch (err) {
                console.error('Error fetching today\'s board:', err);
                setError('Failed to load board data.');
            } finally {
                setLoading(false);
            }
        };

        fetchBasicData();
        fetchBoardData();
    }, []);

    const loadBoard = async (boardNum) => {
        try {
            setLoading(true);
            const board = await getBoardByBoardNumber(boardNum);
            const hasPlayed = getGameDataProperty(boardNum, 'gameScore') !== null;
            setCurrentBoardData({
                boardNum: board['board_num'],
                targetNums: board['target_nums'],
                operators: board['operators'],
                solution_length: board['solution_length']
            });
            setGameDone(hasPlayed);
            setShowEndModal(hasPlayed);
        } catch (err) {
            console.error('Error loading board', boardNum, err);
            setError(`Could not load board ${boardNum}.`);
        } finally {
            setLoading(false);
        }
    };

    const handleMenuToggle = () => {
        setShowBoardMenu((visible) => !visible);
    };

    const handleBoardSelect = (boardNum) => {
        setShowBoardMenu(false);
        if (boardNum === currentBoardData.boardNum) return;
        setPendingBoardChoice(boardNum);
        setShowConfirmModal(true);
    };

    const handleConfirmStart = async () => {
        setShowConfirmModal(false);
        if (pendingBoardChoice) {
            await loadBoard(pendingBoardChoice);
            setPendingBoardChoice(null);
        }
    };

    const handleCancelConfirm = () => {
        setShowConfirmModal(false);
        setPendingBoardChoice(null);
    };

    if (loading) {
        return <div className="main-loading">Loading...</div>;
    }

    if (error) {
        return <div>Error: {error}</div>;
    }

    // Check if currentBoardData has the necessary properties
    const availableBoards = buildBoardOptions(latestBoardNum);
    const isBoardDataValid = currentBoardData.boardNum && currentBoardData.targetNums && currentBoardData.operators && currentBoardData.solution_length;

    return (
        <div className="main-container">
            <Header onMenuToggle={handleMenuToggle} currentBoardNum={currentBoardData.boardNum} />
            {showBoardMenu && (
                <div className="board-menu-overlay" role="dialog" aria-modal="true">
                    <div className="board-menu-panel">
                        <div className="board-menu-header">
                            <h2>Choose a board</h2>
                            <button className="board-menu-close" onClick={() => setShowBoardMenu(false)} aria-label="Close board menu">×</button>
                        </div>
                        <div className="board-menu-grid">
                            {availableBoards.map((boardNum) => (
                                <button
                                    key={boardNum}
                                    className={`board-menu-button ${boardNum === currentBoardData.boardNum ? 'selected' : ''}`}
                                    onClick={() => handleBoardSelect(boardNum)}
                                >
                                    Board {boardNum}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
            <main className="main-content">
                {isBoardDataValid ? (
                    <Game
                        basicUserData={basicUserData}
                        currentBoardData={currentBoardData}
                        gameDone={gameDone}
                        setGameDone={setGameDone}
                        setShowEndModal={setShowEndModal}
                    />
                ) : (
                    <div>No board data available.</div>
                )}
            </main>
            <Footer />

            {showConfirmModal && (
                <div className="confirm-modal" role="dialog" aria-modal="true">
                    <div className="confirm-modal-content">
                        <h2>Ready to play?</h2>
                        <p>Load board #{pendingBoardChoice} and get ready to start.</p>
                        <div className="confirm-actions">
                            <button className="function-button" onClick={handleCancelConfirm}>Cancel</button>
                            <button className="start-button" onClick={handleConfirmStart}>Yes, load board</button>
                        </div>
                    </div>
                </div>
            )}

            {showEndModal && <EndModal setShowEndModal={setShowEndModal} gameDone={gameDone} boardNum={currentBoardData.boardNum} />}
        </div>
    );
};

export default Main;